import React, { useEffect, useState } from "react";
import ProfileLayout from "../../layout/ProfileLayout";
import { profileService } from "../../services";
import { Empty, Modal, Panel, PanelHead, PhoneField, TextField, isValidPhone } from "../../components/ui";

const EMPTY_FORM = {
  contact_name: "",
  contact_phone: "",
  contact_email: "",
  address: "",
  sub_district: "",
  district: "",
  province: "",
  zipcode: "",
  remark: "",
};

export default function AddressPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingUid, setEditingUid] = useState(null);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      setRows(await profileService.addresses());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onChange = (key) => (event) => setForm((prev) => ({ ...prev, [key]: event.target.value }));

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setEditingUid(null);
    setError("");
    setOpen(true);
  };

  const openEdit = (row) => {
    setForm({ ...EMPTY_FORM, ...row });
    setEditingUid(row.uid);
    setError("");
    setOpen(true);
  };

  const persist = async (nextRows) => {
    try {
      await profileService.saveAddresses(nextRows);
      setOpen(false);
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  const save = async () => {
    if (!isValidPhone(form.contact_phone)) {
      setError("เบอร์โทรศัพท์ต้องเป็นตัวเลข 10 หลัก");
      return;
    }
    const next = editingUid
      ? rows.map((row) => (row.uid === editingUid ? { ...row, ...form } : row))
      : [...rows, { ...form, is_default: rows.length === 0, is_active: true }];
    await persist(next);
  };

  const setDefault = (uid) => persist(rows.map((row) => ({ ...row, is_default: row.uid === uid })));

  const remove = (uid) => persist(rows.map((row) => (row.uid === uid ? { ...row, is_active: false, is_default: false } : row)));

  return (
    <ProfileLayout>
      <Panel>
        <PanelHead
          title="📍 ที่อยู่"
          subtitle="ใช้สำหรับจัดส่งสินค้าและออกใบเสร็จ"
          action={
            <button type="button" className="lt-btn lt-btn--navy lt-btn--pill" onClick={openCreate}>
              เพิ่มที่อยู่
            </button>
          }
        />

        {error ? <p className="lt-helper lt-helper--danger">{error}</p> : null}

        {loading ? (
          <Empty text="กำลังโหลดข้อมูล..." />
        ) : rows.length === 0 ? (
          <Empty text="ยังไม่มีที่อยู่เริ่มต้น" />
        ) : (
          rows.map((row) => (
            <div
              key={row.uid || row.contact_name}
              style={{ border: "1px solid var(--lt-line)", borderRadius: 14, padding: 18, marginBottom: 14 }}
            >
              <div className="lt-flex-between lt-flex-wrap">
                <div>
                  <strong>
                    {row.contact_name} ({row.contact_phone}){" "}
                    {row.is_default ? <span className="lt-tag lt-tag--success">ค่าเริ่มต้น</span> : null}
                  </strong>
                  <p className="lt-helper lt-mb-0" style={{ marginTop: 6 }}>
                    {row.address} {row.sub_district} {row.district} {row.province} {row.zipcode}
                  </p>
                </div>
                <div className="lt-flex lt-gap-8">
                  {!row.is_default ? (
                    <button type="button" className="lt-btn lt-btn--ghost lt-btn--pill" onClick={() => setDefault(row.uid)}>
                      ตั้งเป็นค่าเริ่มต้น
                    </button>
                  ) : null}
                  <button type="button" className="lt-btn lt-btn--ghost lt-btn--pill" onClick={() => openEdit(row)}>
                    แก้ไข
                  </button>
                  <button
                    type="button"
                    className="lt-btn lt-btn--ghost lt-btn--pill"
                    style={{ color: "var(--lt-red)" }}
                    onClick={() => remove(row.uid)}
                  >
                    ลบ
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </Panel>

      <Modal
        open={open}
        title={editingUid ? "แก้ไขที่อยู่" : "เพิ่มที่อยู่"}
        onClose={() => setOpen(false)}
        footer={
          <div className="lt-flex lt-gap-12">
            <button type="button" className="lt-btn lt-btn--ghost" style={{ flex: 1 }} onClick={() => setOpen(false)}>
              ยกเลิก
            </button>
            <button type="button" className="lt-btn lt-btn--teal" style={{ flex: 1 }} onClick={save}>
              บันทึก
            </button>
          </div>
        }
      >
        <TextField label="ชื่อผู้รับ" value={form.contact_name} onChange={onChange("contact_name")} />
        <div className="lt-grid-2">
          <PhoneField label="เบอร์โทรศัพท์" value={form.contact_phone} onChange={onChange("contact_phone")} />
          <TextField label="อีเมล" value={form.contact_email} onChange={onChange("contact_email")} />
        </div>
        <TextField label="ที่อยู่" value={form.address} onChange={onChange("address")} />
        <div className="lt-grid-2">
          <TextField label="แขวง/ตำบล" value={form.sub_district} onChange={onChange("sub_district")} />
          <TextField label="เขต/อำเภอ" value={form.district} onChange={onChange("district")} />
          <TextField label="จังหวัด" value={form.province} onChange={onChange("province")} />
          <TextField label="รหัสไปรษณีย์" value={form.zipcode} onChange={onChange("zipcode")} />
        </div>
        <TextField label="หมายเหตุ" value={form.remark} onChange={onChange("remark")} />
        {error ? <p className="lt-helper lt-helper--danger">{error}</p> : null}
      </Modal>
    </ProfileLayout>
  );
}
